import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Lock, Eye, EyeOff, CheckCircle, XCircle, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState('');
  const [success, setSuccess] = useState(false);
  const [email, setEmail] = useState('');

  useEffect(() => {
    if (!token) {
      setVerifying(false);
      setTokenError('No se proporcionó un token de recuperación');
      return;
    }

    verifyToken();
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await api.verifyResetToken(token);
      if (response.valid) {
        setTokenValid(true);
        setEmail(response.email);
      } else {
        setTokenError(response.message || 'Token inválido o expirado');
      }
    } catch (error) {
      setTokenError('Error al verificar el token');
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password.length < 8) {
      toast.error('La contraseña debe tener al menos 8 caracteres');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    setLoading(true);
    try {
      await api.resetPassword(token, password);
      setSuccess(true);
      toast.success('Contraseña actualizada exitosamente');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al restablecer contraseña');
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#F5F5F0' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: '#1A4D2E' }}></div>
          <p className="text-gray-600">Verificando enlace...</p>
        </div>
      </div>
    );
  }

  // Invalid token
  if (!tokenValid && !success) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#F5F5F0' }}>
        <div className="bg-white border border-border shadow-sm rounded-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 bg-red-100">
            <XCircle className="w-8 h-8 text-red-600" />
          </div>
          
          <h1 className="text-2xl font-bold mb-4 text-red-600">
            Enlace inválido
          </h1>
          
          <p className="text-gray-600 mb-6">
            {tokenError}
          </p>
          
          <div className="space-y-3">
            <Link to="/forgot-password" className="block">
              <Button 
                className="w-full"
                style={{ backgroundColor: '#1A4D2E' }}
              >
                Solicitar nuevo enlace
              </Button>
            </Link>
            
            <Link to="/login" className="block">
              <Button variant="outline" className="w-full">
                Volver al inicio de sesión
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#F5F5F0' }}>
        <div className="bg-white border border-border shadow-sm rounded-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: '#E8F5E9' }}>
            <CheckCircle className="w-8 h-8" style={{ color: '#1A4D2E' }} />
          </div>
          
          <h1 className="text-2xl font-bold mb-4" style={{ color: '#1A4D2E' }}>
            ¡Contraseña actualizada!
          </h1>
          
          <p className="text-gray-600 mb-6">
            Tu contraseña ha sido restablecida exitosamente. Ya puedes iniciar sesión con tu nueva contraseña.
          </p>
          
          <Link to="/login">
            <Button 
              className="w-full"
              style={{ backgroundColor: '#1A4D2E' }}
            >
              Iniciar sesión
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Reset form
  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#F5F5F0' }}>
      <div className="bg-white border border-border shadow-sm rounded-lg p-8 max-w-md w-full">
        <Link to="/login" className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-6">
          <ArrowLeft className="w-4 h-4" />
          Volver al inicio de sesión
        </Link>
        
        <div className="text-center mb-8">
          <img src="/assets/logo.png" alt="contractcheck360" className="h-12 w-auto mx-auto mb-4" />
          <h1 className="text-2xl font-bold" style={{ color: '#1A4D2E' }}>
            Crear nueva contraseña
          </h1>
          {email && (
            <p className="text-gray-600 mt-2">
              Para: <strong>{email}</strong>
            </p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nueva contraseña
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Mínimo 8 caracteres"
                className="pl-10 pr-10"
                data-testid="reset-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Confirmar contraseña
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Repite la contraseña"
                className="pl-10"
                data-testid="reset-confirm-input"
              />
            </div>
            {confirmPassword && password !== confirmPassword && (
              <p className="text-sm text-red-600 mt-1">Las contraseñas no coinciden</p>
            )}
          </div>

          <Button
            type="submit"
            disabled={loading || password.length < 8 || password !== confirmPassword}
            className="w-full"
            style={{ backgroundColor: '#1A4D2E' }}
            data-testid="reset-submit-btn"
          >
            {loading ? 'Actualizando...' : 'Restablecer contraseña'}
          </Button>
        </form>

        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
          <p className="text-sm text-gray-600">
            <strong>Requisitos de contraseña:</strong>
          </p>
          <ul className="text-sm text-gray-500 mt-2 space-y-1">
            <li className={password.length >= 8 ? 'text-green-600' : ''}>
              {password.length >= 8 ? '✓' : '○'} Mínimo 8 caracteres
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
